import React, { useCallback, useEffect, useRef, useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import {
  Alert,
  Snackbar,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";

import {
  acceptTicket,
  declineTicket,
  downloadTicket,
  verifyUserAndTicketAccessibilityMatch,
} from "../../../services/TicketService";
import { UserBookingInterest } from "../../../models/BookingInterest";
import { BookingCard, Button, colorArray, MeshColours, Modal, priorityColorArray } from "../../../components";
import { Modals, useModal } from "../../../contexts/ModalContext";
import { BookingInterestsContainer, CTAContainer } from "./BookingInterestManagement.styles";
import { declineAttendanceByIdOrToken } from "src/services/AttendanceService";

interface BookingInterestManagementProps extends WithRouterProps {
  bookingInterests: UserBookingInterest[];
  fetchBookingInterests: Function;
}

const BookingInterestManagement: React.FC<BookingInterestManagementProps> = ({
  bookingInterests,
  fetchBookingInterests,
  router
}) => {
  const [actionName, setActionName] = useState('accept');
  const [snackbarErrorOpen, setSnackbarErrorOpen] = useState(false);
  const [snackbarSuccessOpen, setSnackbarSuccessOpen] = useState(false);
  const [snackbarUpdateSuccessOpen, setSnackbarUpdateSuccessOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<React.ReactElement | string | undefined>(undefined);
  const [acceptTicketBookingInterest, setAcceptTicketBookingInterest] = useState<UserBookingInterest>();
  const [pendingTicketGenerationId, setPendingTicketGenerationId] = useState<number | null>(null);
  const { openModal, setCurrentTexts, modalBookingData } = useModal();

  const ticketPollInterval = 1000;
  const maxTicketPollingAttempts = 60;
  
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fetchBookingInterestsRef = useRef(fetchBookingInterests);
  const bookingInterestsRef = useRef(bookingInterests);
  const modalBookingDataRef = useRef(modalBookingData);
  const ticketPollStateRef = useRef({
    ticketId: null as number | null,
    ticketWasCleared: false,
    attempts: 0,
  });

  const confirm = useConfirm();
 
  useEffect(() => {
    fetchBookingInterestsRef.current = fetchBookingInterests;
    bookingInterestsRef.current = bookingInterests;
    modalBookingDataRef.current = modalBookingData;
  }, [fetchBookingInterests, bookingInterests, modalBookingData]);

  const stopTicketPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setPendingTicketGenerationId(null);
    ticketPollStateRef.current = {
      ticketId: null,
      ticketWasCleared: false,
      attempts: 0,
    };
  }, []);

  useEffect(() => {
    if (!pendingTicketGenerationId) {
      stopTicketPolling();
      return;
    }

    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    pollTimerRef.current = setInterval(async () => {
      const pollState = ticketPollStateRef.current;
      const bookingInterest = bookingInterestsRef.current.find(
        (item) => item.id === pendingTicketGenerationId
      );
      const currentTicketId = bookingInterest?.ticket?.id ?? null;
      const isTicketReady = Boolean(currentTicketId);
      const ticketIdChanged = pollState.ticketId != null &&
        currentTicketId != null &&
        currentTicketId !== pollState.ticketId;
      const ticketRegenerated = isTicketReady &&
        (ticketIdChanged || pollState.ticketWasCleared || pollState.ticketId == null);

      if (bookingInterest && bookingInterest.ticket == null) {
        pollState.ticketWasCleared = true;
      }

      if (ticketRegenerated || pollState.attempts >= maxTicketPollingAttempts) {
        stopTicketPolling();
        return;
      }

      pollState.attempts += 1;
      await fetchBookingInterestsRef.current();
    }, ticketPollInterval);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [pendingTicketGenerationId, ticketPollInterval, maxTicketPollingAttempts, stopTicketPolling]);

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarErrorOpen(false);
    setSnackbarSuccessOpen(false);
    setSnackbarUpdateSuccessOpen(false);
  };

  const handleVerifyUserAndTicketAccessibilityMatch = async (bookingInterest: UserBookingInterest) => {
    setAcceptTicketBookingInterest(bookingInterest);
    const res = await verifyUserAndTicketAccessibilityMatch(bookingInterest.id!);
    if (res.error) {
      setCurrentTexts({
        mainBody: res.error
      })
      openModal(Modals.AcceptTicket, onAcceptTicketModalClick)
    } else {
      await handleAcceptTicket(bookingInterest);
      setAcceptTicketBookingInterest(undefined);
    }
  };

  const handleAcceptTicket = async (bookingInterest: UserBookingInterest) => {
    setActionName("accept")
    const res = await acceptTicket(bookingInterest.id!);
    if (res) {
      await fetchBookingInterests();
      setSnackbarUpdateSuccessOpen(false);
      setSnackbarSuccessOpen(true);
    } else {
      setSnackbarErrorOpen(true);
    }
  };

  const handleDeclineTicket = async (bookingInterest: UserBookingInterest) => {
    setActionName("decline")
    confirm({
      description: "Are you going to decline the ticket? Caution: This action is permanent and cannot be reversed.",
    }).then(async () => {
      const res = await declineTicket(bookingInterest.id!);
      if (res) {
        await fetchBookingInterests();
        setSnackbarUpdateSuccessOpen(false);
        setSnackbarSuccessOpen(true);
      } else {
        setSnackbarErrorOpen(true);
      }

      return new Promise<void>((resolve, reject) => {
        resolve();
      });
    });
  };

  const handleDeclineAttendance = async (bookingInterest: UserBookingInterest) => {
    setActionName("check-in decline")
    confirm({
      description: "Are you going to decline the ticket check-in? Caution: This action is permanent and cannot be reversed.",
    }).then(async () => {
      const res = await declineAttendanceByIdOrToken(bookingInterest.id!);
      if (res) {
        await fetchBookingInterests();
        setSnackbarUpdateSuccessOpen(false);
        setSnackbarSuccessOpen(true);
      } else {
        setSnackbarErrorOpen(true);
      }

      return new Promise<void>((resolve, reject) => {
        resolve();
      });
    });
  };

  const handleDownloadTicket = async (userEmail: string, ticketId: number) => {
    setActionName("download")
    const res = await downloadTicket(userEmail, ticketId);
    if (res) {
      setSnackbarUpdateSuccessOpen(false);
      setSnackbarSuccessOpen(true);
    } else {
      setSnackbarErrorOpen(true);
    }

    return new Promise<void>((resolve, reject) => {
      resolve();
    });
  };

  const onAcceptTicketModalClick = async () => {
    if (!acceptTicketBookingInterest) {
      return;
    }
    await handleAcceptTicket(acceptTicketBookingInterest);
    setAcceptTicketBookingInterest(undefined);
  };

  const onModalCTAClick = async (successful?: boolean, message?: React.ReactElement | string ) => {
    if (successful) {
      setSnackbarSuccessOpen(false);
      setSnackbarMessage(message)
      setSnackbarUpdateSuccessOpen(true);
      
      const bookingInterestId = modalBookingDataRef.current?.bookingInterest?.id;
      if (bookingInterestId) {
        const currentTicketId = bookingInterestsRef.current.find(
          (item) => item.id === bookingInterestId
        )?.ticket?.id ?? null;
        setPendingTicketGenerationId(bookingInterestId);

        ticketPollStateRef.current = {
          ticketId: currentTicketId,
          ticketWasCleared: false,
          attempts: 0,
        };
      }
    }
    await fetchBookingInterests();
  }

  return (
    <>
      {
        bookingInterests?.length > 0 ?
          <BookingInterestsContainer>
            {bookingInterests.map((bookingInterest, index) => (
              <BookingCard
                key={`booking-interest-${bookingInterest.id}`}
                bookingInterest={bookingInterest}
                isTicketGenerating={pendingTicketGenerationId === bookingInterest.id}
                colour={bookingInterest.allocationSession?.allocation?.type == "Priority" ?
                   priorityColorArray[index % priorityColorArray.length] : 
                   colorArray[index % colorArray.length]}
                handleAcceptTicket={handleVerifyUserAndTicketAccessibilityMatch} //temporary - Pending on Tiket Offer pages
                handleDeclineTicket={handleDeclineTicket} //temporary
                handleDownloadTicket={handleDownloadTicket} //temporary
                handleDeclineAttendance={handleDeclineAttendance}
                onModalAction={onModalCTAClick}
              />
            ))}
          </BookingInterestsContainer> :
          <>
            <CTAContainer>
              <Typography variant="body1">
                You don't have any tickets yet.
              </Typography>
              <Button
                data-cy="login-button"
                onClick={() => router.push(`/audience`)}
              >
                Apply To Shows Here
              </Button>
            </CTAContainer>
          </>
      }
      <Modal />
      <Snackbar
        open={snackbarErrorOpen}
        autoHideDuration={10000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity="error"
          sx={{ width: "100%", fontSize: "16px" }}
        >
          Ticket {actionName} failed!
        </Alert>
      </Snackbar>
      <Snackbar
        open={snackbarSuccessOpen}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          sx={{ width: "100%", fontSize: "16px", marginTop: "70px" }}
        >
          Ticket {actionName} successful!
        </Alert>
      </Snackbar>
      <Snackbar
        open={snackbarUpdateSuccessOpen}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          sx={{ width: "100%", fontSize: "16px", marginTop: "70px" }}
        >
          { snackbarMessage ? snackbarMessage : "Tickets updated successfully!" }
        </Alert>
      </Snackbar>
    </>
  );
};

export default withRouter(BookingInterestManagement);
